import Select, {GroupBase, Props as SelectProps} from 'react-select'
import {Response} from '../../_metronic/helpers'
import {ISelect} from '../../coreGlobal/models/default'

export type ProductOption = ISelect & {
  id: number
  gender: string | null
  active: boolean
}

// Produto como vem do index do admin (admin/products)
export type AdminProductsResponse = Response<
  Array<{id: number; name: string; gender: string | null; active: boolean; sessionTime: number | null}>
>

export const toProductOptions = (response: AdminProductsResponse): ProductOption[] =>
  (response.data ?? [])
    .map((product) => ({
      id: product.id,
      value: String(product.id),
      label: product.name,
      gender: product.gender,
      active: product.active,
      session_time: product.sessionTime ? String(product.sessionTime) : undefined,
    }))
    // Inativos no fim, mantendo a ordem alfabética dentro de cada grupo
    .sort((a, b) => Number(b.active) - Number(a.active))

const GENDERS: Record<string, {label: string; className: string}> = {
  m: {label: 'Masculino', className: 'badge-light-primary'},
  f: {label: 'Feminino', className: 'badge-light-danger'},
  o: {label: 'Ambos', className: 'badge-light-info'},
}

const genderOf = (product: ProductOption) =>
  product.gender ? GENDERS[product.gender.toLowerCase()] : undefined

// Há vários produtos com o mesmo nome; gênero, status e tempo padrão ajudam a diferenciá-los
const formatOptionLabel = (product: ProductOption, {context}) => {
  const gender = genderOf(product)

  if (context === 'value') {
    return [product.label, gender?.label, !product.active && 'Inativo'].filter(Boolean).join(' · ')
  }

  return (
    <div className={product.active ? '' : 'opacity-50'}>
      <div className='fw-bold'>{product.label}</div>
      <div className='d-flex align-items-center gap-2 fs-7'>
        {gender && <span className={`badge ${gender.className}`}>{gender.label}</span>}
        <span className={`badge ${product.active ? 'badge-light-success' : 'badge-light'}`}>
          {product.active ? 'Ativo' : 'Inativo'}
        </span>
        <span className='text-muted'>
          {product.session_time ? `${product.session_time} min · ` : ''}#{product.id}
        </span>
      </div>
    </div>
  )
}

const filterOption = ({data}: {data: ProductOption}, input: string) => {
  const search = input.trim().toLowerCase()
  return !search || data.label.toLowerCase().includes(search) || String(data.id) === search
}

export const ProductSelect = <IsMulti extends boolean = false>(
  props: SelectProps<ProductOption, IsMulti, GroupBase<ProductOption>>
) => (
  <Select<ProductOption, IsMulti>
    className='react-select-styled react-select-solid'
    classNamePrefix='react-select'
    formatOptionLabel={formatOptionLabel}
    filterOption={filterOption}
    placeholder='Selecione o produto'
    noOptionsMessage={() => 'Nenhum produto disponível'}
    menuPortalTarget={document.body}
    styles={{menuPortal: (base) => ({...base, zIndex: 9999})}}
    {...props}
  />
)
